import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Plus, Edit, Trash2, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { MCPServer } from '@/types';
import { clsx } from 'clsx';
import { apiClient } from '@/utils/apiClient';
import { useTranslation, formatCount } from '@/i18n';

// Status check interval: 60 seconds (longer to reduce overhead)
const STATUS_CHECK_INTERVAL_MS = 60000;

// Local status type (separate from store to avoid re-render cascades)
interface LocalServerStatus {
  id: string;
  status: 'connected' | 'disconnected' | 'error';
  toolCount: number;
}

interface BackendHealth {
  status: string;
  mcp_client: string;
}

export const MCPServerSettings: React.FC = () => {
  const { t, language } = useTranslation();
  // Only subscribe to the store actions we need - use shallow comparison
  const mcpServers = useAppStore(state => state.mcpServers);
  const addMCPServer = useAppStore(state => state.addMCPServer);
  const updateMCPServer = useAppStore(state => state.updateMCPServer);
  const deleteMCPServer = useAppStore(state => state.deleteMCPServer);
  const toggleMCPServer = useAppStore(state => state.toggleMCPServer);
  
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // LOCAL status state - doesn't affect store, won't cascade re-renders
  const [backendHealth, setBackendHealth] = useState<BackendHealth | null>(null);
  const [localStatuses, setLocalStatuses] = useState<Map<string, LocalServerStatus>>(new Map());
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  
  // Abort controller for cleanup
  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  const [formData, setFormData] = useState<{
    name: string;
    url: string;
    transport: 'http' | 'sse' | 'websocket';
    auth: {
      type: 'bearer' | 'basic' | 'apikey';
      token?: string;
      username?: string;
      password?: string;
    };
  }>({
    name: '',
    url: '',
    transport: 'sse',
    auth: {
      type: 'bearer',
      token: '',
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      transport: 'sse' as 'http' | 'sse' | 'websocket',
      auth: {
        type: 'bearer' as 'bearer' | 'basic' | 'apikey',
        token: '',
      },
    });
  };

  // Check status without updating the main store
  const checkStatusLocally = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      // Check backend health
      const health = await apiClient.healthCheck();
      if (!isMountedRef.current) return;
      setBackendHealth(health);
      
      // Get server statuses from backend
      if (health?.status === 'ok') {
        const serverInfos = await apiClient.getMCPServers(false);
        if (!isMountedRef.current) return;

        // Update LOCAL status map only
        const newStatuses = new Map<string, LocalServerStatus>();
        for (const info of serverInfos) {
          newStatuses.set(info.name.toLowerCase(), {
            id: info.name,
            status: info.status as any,
            toolCount: info.tools?.length || 0,
          });
        }
        setLocalStatuses(newStatuses);
      }
      
      setLastChecked(new Date());
    } catch (error) {
      console.error('Status check failed:', error);
      if (isMountedRef.current) {
        setBackendHealth({ status: 'error', mcp_client: 'not available' });
      }
    }
  }, []);

  // Full refresh: updates both local status AND the store
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const health = await apiClient.healthCheck();
      setBackendHealth(health);
      
      if (health?.status === 'ok') {
        const serverInfos = await apiClient.getMCPServers(true);
        
        // Update local statuses
        const newStatuses = new Map<string, LocalServerStatus>();
        for (const info of serverInfos) {
          newStatuses.set(info.name.toLowerCase(), {
            id: info.name,
            status: info.status as any,
            toolCount: info.tools?.length || 0,
          });
        }
        setLocalStatuses(newStatuses);
        
        // ALSO update the store (only on manual refresh)
        for (const server of mcpServers) {
          const info = serverInfos.find(
            s => s.name.toLowerCase() === server.name.toLowerCase() ||
                 s.url.replace(/\/?$/, '/') === server.url.replace(/\/?$/, '/')
          );
          if (info) {
            updateMCPServer(server.id, {
              status: info.status as any,
              tools: info.tools?.map(t => ({
                name: t.name,
                description: t.description,
                inputSchema: {},
              })) as any,
            });
          }
        }
      }
      
      setLastChecked(new Date());
    } catch (error) {
      console.error('Failed to refresh:', error);
      setBackendHealth({ status: 'error', mcp_client: 'not available' });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initial check and polling setup
  useEffect(() => {
    isMountedRef.current = true;
    
    // Check once on mount
    checkStatusLocally();
    
    // Set up polling interval
    const intervalId = setInterval(checkStatusLocally, STATUS_CHECK_INTERVAL_MS);
    
    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []); // Empty deps - only runs on mount/unmount

  // Get the display status for a server (local status takes precedence)
  const getServerDisplayStatus = useCallback((server: MCPServer) => {
    const localStatus = localStatuses.get(server.name.toLowerCase());
    return localStatus?.status || server.status;
  }, [localStatuses]);

  const getServerToolCount = useCallback((server: MCPServer) => {
    const localStatus = localStatuses.get(server.name.toLowerCase());
    return localStatus?.toolCount || server.tools?.length || 0;
  }, [localStatuses]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingId) {
      updateMCPServer(editingId, {
        ...formData,
        enabled: true,
        status: 'disconnected',
      });
      setEditingId(null);
    } else {
      addMCPServer({
        ...formData,
        enabled: true,
        status: 'disconnected',
      });
      setIsAdding(false);
    }
    
    resetForm();
    
    // Refresh after adding/editing
    setTimeout(handleRefresh, 500);
  };

  const handleEdit = (server: MCPServer) => {
    setFormData({
      name: server.name,
      url: server.url,
      transport: server.transport,
      auth: server.auth || { type: 'bearer', token: '' },
    });
    setEditingId(server.id);
    setIsAdding(true);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    resetForm();
  };

  const getStatusIcon = (status: MCPServer['status']) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status: MCPServer['status']) => {
    switch (status) {
      case 'connected':
        return t.settings.serverConnected;
      case 'error':
        return t.settings.serverError;
      default:
        return t.settings.serverDisconnected;
    }
  };

  const formatBackendStatus = (status: string) => {
    const map: Record<string, string> = {
      ok: t.settings.healthStatusOk,
      ready: t.settings.healthStatusReady,
      error: t.settings.healthStatusError,
      degraded: t.settings.healthStatusDegraded,
      unconfigured: t.settings.healthStatusUnconfigured,
      initializing: t.settings.healthStatusInitializing,
      unknown: t.settings.healthUnknown,
    };
    return map[status] || t.settings.healthUnknown;
  };

  const formatMcpClientStatus = (status: string) => {
    const map: Record<string, string> = {
      connected: t.settings.mcpClientConnected,
      'not available': t.settings.mcpClientUnavailable,
    };
    return map[status] || t.settings.mcpClientUnavailable;
  };

  const backendHealthy = backendHealth?.status === 'ok' || backendHealth?.status === 'ready';

  // Memoize server list to prevent unnecessary re-renders
  const serverList = useMemo(() => mcpServers, [mcpServers]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t.settings.mcpServers}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t.settings.configureMCPDescription}
          </p>
          {backendHealth && (
            <div className="mt-2 flex items-center space-x-4 text-xs">
              <span className={clsx(
                'flex items-center space-x-1',
                backendHealthy ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                <div className={clsx(
                  'w-2 h-2 rounded-full',
                  backendHealthy ? 'bg-green-500' : 'bg-red-500'
                )} />
                <span>{t.settings.backendStatus}: {formatBackendStatus(backendHealth.status)}</span>
              </span>
              <span className={clsx(
                'flex items-center space-x-1',
                backendHealth.mcp_client === 'connected' ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
              )}>
                <div className={clsx(
                  'w-2 h-2 rounded-full',
                  backendHealth.mcp_client === 'connected' ? 'bg-green-500' : 'bg-gray-400'
                )} />
                <span>{t.settings.mcpStatus}: {formatMcpClientStatus(backendHealth.mcp_client || '')}</span>
              </span>
              {lastChecked && (
                <span className="text-gray-400 dark:text-gray-500">
                  {t.settings.checkedAt} {lastChecked.toLocaleTimeString()}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md transition-colors disabled:opacity-50"
          >
            <RefreshCw className={clsx('mr-2 h-4 w-4', isRefreshing && 'animate-spin')} />
            {t.common.refresh}
          </button>
          {!isAdding && (
            <button
              onClick={() => setIsAdding(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t.settings.addServer}
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit Form */}
      {isAdding && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
            {editingId ? t.settings.editServer : t.settings.addServer}
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.settings.serverName}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder={t.settings.serverPlaceholder}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.settings.serverTransport}
                </label>
                <select
                  value={formData.transport}
                  onChange={(e) => setFormData({ ...formData, transport: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="sse">{t.settings.transportSse}</option>
                  <option value="http">{t.settings.transportHttp}</option>
                  <option value="websocket">{t.settings.transportWebsocket}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.settings.serverUrl}
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder={t.settings.serverUrlPlaceholder}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.settings.authenticationToken}
              </label>
              <input
                type="password"
                value={formData.auth.token}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  auth: { ...formData.auth, token: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder={t.settings.authenticationTokenPlaceholder}
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                {editingId ? t.common.update : t.settings.addServer}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md transition-colors"
              >
                {t.common.cancel}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Server List */}
      <div className="space-y-3">
        {serverList.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>{t.settings.noServersConfigured}</p>
            <p className="text-sm">{t.settings.addServerToEnable}</p>
          </div>
        ) : (
          serverList.map((server) => {
            const displayStatus = getServerDisplayStatus(server);
            const toolCount = getServerToolCount(server);
            
            return (
              <div
                key={server.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        {server.name}
                      </h4>
                      <span className={clsx(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        server.transport === 'sse' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        server.transport === 'http' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                      )}>
                        {server.transport.toUpperCase()}
                      </span>
                      <div className="flex items-center space-x-1">
                        {getStatusIcon(displayStatus)}
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {getStatusText(displayStatus)}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {server.url}
                    </p>
                    {toolCount > 0 && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {formatCount(language, toolCount, t.settingsCounts.toolsAvailable)}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={server.enabled}
                        onChange={() => toggleMCPServer(server.id)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                    
                    <button
                      onClick={() => handleEdit(server)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title={t.settings.editServer}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => deleteMCPServer(server.id)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title={t.settings.deleteServer}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
